
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { ApplicantAssessment } from "@/store/modules/recruiter/interfaces";
import { redo_assessment_request } from "@/store/apis/recruiter";

export default Vue.extend({
  name: "CandidateAiAssessments",
  data() {
    return {
      loading: false,
      assessments: [] as ApplicantAssessment[],
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      startingAssessmentId: null as number | null
    };
  },
  computed: {
    totalPages(): number {
      // Use totalPages from API response if available, otherwise calculate
      if (this.applicantAssessments?.totalPages) {
        return this.applicantAssessments.totalPages;
      }
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    ...mapGetters("auth", {
      user: "GET_USER_DETAILS",
      get_company_details: "GET_COMPANY_DETAILS"
    }),
    ...mapGetters("recruiter", {
      applicantAssessments: "APPLICANT_ASSESSMENTS"
    }),
    // Assignment-driven tenants (no company assessment_setting) have no approval gate.
    is_assignment_driven(): boolean {
      return !this.get_company_details?.storage_path?.assessment_setting
        ?.candidates_english;
    }
  },
  async created() {
    await this.loadAssessments();
  },
  methods: {
    ...mapActions("recruiter", {
      fetchApplicantAssessments: "FETCH_APPLICANT_ASSESSMENTS"
    }),
    async loadAssessments() {
      this.loading = true;
      try {
        // Get the current user's applicant ID
        const applicantId = this.user?.id;
        if (!applicantId) {
          throw new Error("User ID not found");
        }

        // Fetch assessments for the current applicant with pagination
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage
        };

        const success = await this.fetchApplicantAssessments({
          applicant_id: applicantId,
          params: params
        });
        if (success) {
          // Handle paginated response structure
          if (this.applicantAssessments?.data) {
            this.assessments = this.applicantAssessments.data;
            this.totalItems = this.applicantAssessments.total || 0;
            console.log("Paginated response:", {
              assessments: this.assessments.length,
              total: this.totalItems,
              currentPage: this.currentPage,
              totalPages: this.totalPages
            });
          } else {
            // Handle non-paginated response (backward compatibility)
            this.assessments = this.applicantAssessments || [];
            this.totalItems = this.assessments.length;
            console.log("Non-paginated response:", {
              assessments: this.assessments.length,
              total: this.totalItems
            });
          }
        }
      } catch (error) {
        console.error("Failed to load assessments:", error);
        this.$store.commit("ROOT_ERROR", error, { root: true });
      } finally {
        this.loading = false;
      }
    },
    async onPageChange(page: number) {
      this.currentPage = page;
      await this.loadAssessments();
    },
    getDifficultyColor(difficulty: string): string {
      switch (difficulty) {
        case "beginner":
          return "green";
        case "intermediate":
          return "orange";
        case "advanced":
          return "red";
        default:
          return "grey";
      }
    },
    async requestCameraAndMicThenNavigate(assessment: ApplicantAssessment) {
      this.startingAssessmentId = assessment.assessment.id;
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.browser-unsupport-camera").toString(),
            { root: true }
          );
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        if (stream && stream.getTracks().length > 0) {
          stream.getTracks().forEach((t) => t.stop());
          this.$router.push({
            name: "candidate-ai-assessment",
            params: { id: assessment.assessment.id.toString() }
          });
        }
      } catch (e) {
        const err = e as { name?: string };
        if (
          err?.name === "NotAllowedError" ||
          err?.name === "PermissionDeniedError"
        ) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.permission-denied").toString(),
            { root: true }
          );
        } else if (
          err?.name === "NotFoundError" ||
          err?.name === "DevicesNotFoundError"
        ) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.no-camera-device").toString(),
            { root: true }
          );
        } else {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.permission-denied").toString(),
            { root: true }
          );
        }
      } finally {
        this.startingAssessmentId = null;
      }
    },
    startAssessment(assessment: ApplicantAssessment) {
      if (this.isCompleted(assessment)) return;
      this.$router.push({
        name: "candidate-ai-assessment",
        params: { id: assessment.assessment.id.toString() }
      });
    },
    async handlePrimaryAction(assessment: ApplicantAssessment) {
      if (this.isRedo(assessment)) return;
      if (this.isCompleted(assessment)) return;
      if (this.isAdminApprovalRequired(assessment)) return;
      if (this.isCancelled(assessment)) {
        await this.requestRedo(assessment);
        return;
      }

      await this.requestCameraAndMicThenNavigate(assessment);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isCompleted(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "COMPLETED" || status === "RESULT_PROCESSING";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isCancelled(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "CANCELLED";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isRedo(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "REDO";
    },
    /** True when assessment is in progress and admin has approved resume, so candidate should continue where they left off. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canResume(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      if (status !== "IN_PROGRESS") return false;
      // Assignment-driven tenants have no admin-approval gate — always resumable.
      if (this.is_assignment_driven) return true;
      const adminStatus =
        assessment?.resume_admin_status ??
        assessment?.result?.resume_admin_status ??
        null;
      return adminStatus === "APPROVED";
    },
    /** True when assessment is in progress and user needs admin approval to resume (left in between) */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isAdminApprovalRequired(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      if (status !== "IN_PROGRESS") return false;
      // Assignment-driven tenants have no admin-approval gate.
      if (this.is_assignment_driven) return false;
      const adminStatus =
        assessment?.resume_admin_status ??
        assessment?.result?.resume_admin_status ??
        null;
      return adminStatus !== "APPROVED";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extractStatus(assessment: any): string | undefined {
      const directStatus = assessment?.status;
      const resultStatus = assessment?.result?.status;
      const firstResultStatus = Array.isArray(assessment?.results)
        ? assessment.results[0]?.status
        : undefined;
      return directStatus || resultStatus || firstResultStatus;
    },
    async requestRedo(_assessment: ApplicantAssessment) {
      try {
        const response = await redo_assessment_request(
          _assessment.assessment.id
        );
        if (response) {
          await this.loadAssessments();
          this.$store.commit(
            "ROOT_NOTIFICATION",
            this.$t("assessments.redo_notification").toString(),
            { root: true }
          );
        }
      } catch (error) {
        // Surface backend failure instead of silently breaking the click handler
        // so candidate understands that retake could not be requested.
        // eslint-disable-next-line no-console
        console.error("Failed to request assessment redo:", error);
        this.$store.commit(
          "ROOT_ERROR",
          this.$t("assessments.redo_failed").toString(),
          { root: true }
        );
      }
    },
    formatDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    }
  }
});
